import type {
  ArtistFullProfileInfoDto,
  ClubFullProfileInfoDto
} from '@customTypes/types';
import { showFullArtistProfile } from '@features/work/api/show-full-artist-profile';
import { PencilIcon } from '@heroicons/react/24/outline';
import {
  Title,
  AppShell,
  Stack,
  Center,
  Loader,
  Modal,
  Button,
  Group,
  Collapse,
  Text,
  Box,
  ActionIcon,
  Flex,
  Textarea
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { useClubRatingsQuery } from '@shared/api/requests/club/ratings/get';
import { useModerateClubRating } from '@shared/api/requests/club/ratings/moderate';
import { ExpandableText } from '@shared/helper/ExpandableComment.tsx';
import type { UseQueryResult } from '@tanstack/react-query';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import type { AxiosResponse } from 'axios';
import dayjs from 'dayjs';
import { useState, useMemo } from 'react';

import { showFullClubProfile } from '@/features/work/api/show-full-club-profile';

type ReviewsPageProps = {
  clubId: number;
  artistProfileId: number;
};

type SortBy = 'rating' | 'date' | 'artist' | 'club';
type SortOrder = 'asc' | 'desc';

export const Reviews = ({ clubId, artistProfileId }: ReviewsPageProps) => {
  const { data, isLoading } = useClubRatingsQuery({ clubId, artistProfileId });
  const ratings = data?.data.items ?? [];
  const isMobile = useMediaQuery('(max-width: 768px)');

  const [editing, setEditing] = useState<(typeof ratings)[0] | null>(null);
  const [editComment, setEditComment] = useState('');
  const [editReply, setEditReply] = useState('');
  const [showRating, setShowRating] = useState<{ [key: number]: boolean }>({});

  const [sortBy, setSortBy] = useState<SortBy>('rating');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

  // const deleteClubMutation = useDeleteClubRating();
  // const deleteArtistMutation = useDeleteArtistRating();
  const moderateClubMutation = useModerateClubRating();
  const queryClient = useQueryClient();

  // Memoize queries to keep Hooks order stable
  const clubQueries = useMemo(
    () =>
      ratings.map((r) => ({
        queryKey: ['showFullClubProfile', r.clubProfileId.toString()],
        queryFn: async (): Promise<ClubFullProfileInfoDto> => {
          const response: AxiosResponse<ClubFullProfileInfoDto> =
            await showFullClubProfile({
              params: { profileId: r.clubProfileId.toString() }
            });
          return response.data;
        }
      })),
    [ratings]
  );

  const artistQueries = useMemo(
    () =>
      ratings.map((r) => ({
        queryKey: ['artistProfile', r.artistProfileId.toString()],
        queryFn: async (): Promise<ArtistFullProfileInfoDto> => {
          const response: AxiosResponse<ArtistFullProfileInfoDto> =
            await showFullArtistProfile({
              params: { profileId: r.artistProfileId.toString() }
            });
          return response.data;
        }
      })),
    [ratings]
  );

  const clubProfiles: UseQueryResult<ClubFullProfileInfoDto, unknown>[] =
    useQueries({ queries: clubQueries });
  const artistProfiles: UseQueryResult<ArtistFullProfileInfoDto, unknown>[] =
    useQueries({ queries: artistQueries });

  const handleEdit = (r: (typeof ratings)[0]) => {
    setEditing(r);
    setEditComment(r.comment ?? '');
    setEditReply(r.reply ?? '');
  };

  const handleSave = () => {
    if (!editing) return;

    moderateClubMutation.mutate(
      {
        clubRatingId: editing.clubRatingId,
        comment: editComment,
        reply: editReply
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: ['clubRatings', { clubId, artistProfileId }]
          });
          setEditing(null);
        }
      }
    );
  };

  const sortedRatings = useMemo(() => {
    // Attach names to each rating
    const ratingsWithNames = ratings.map((r, i) => ({
      ...r,
      artistName: artistProfiles[i]?.data?.info?.firstName ?? '-',
      clubName: clubProfiles[i]?.data?.clubInfo?.name ?? '-'
    }));

    // Sort
    return ratingsWithNames.sort((a, b) => {
      switch (sortBy) {
        case 'rating': {
          const aAvg = (a.staffAndManagement + a.dressingRoom + a.housing) / 3;
          const bAvg = (b.staffAndManagement + b.dressingRoom + b.housing) / 3;
          return sortOrder === 'asc' ? aAvg - bAvg : bAvg - aAvg;
        }
        case 'date': {
          const aTime = new Date(a.createdAt).getTime();
          const bTime = new Date(b.createdAt).getTime();
          return sortOrder === 'asc' ? aTime - bTime : bTime - aTime;
        }
        case 'artist':
          return sortOrder === 'asc'
            ? a.artistName.localeCompare(b.artistName)
            : b.artistName.localeCompare(a.artistName);
        case 'club':
          return sortOrder === 'asc'
            ? a.clubName.localeCompare(b.clubName)
            : b.clubName.localeCompare(a.clubName);
        default:
          return 0;
      }
    });
  }, [ratings, artistProfiles, clubProfiles, sortBy, sortOrder]);

  const handleHeaderClick = (field: SortBy) => {
    if (sortBy === field) setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    else setSortBy(field);
  };

  const renderHeader = (label: string, field: SortBy) => {
    const arrow = sortBy === field ? (sortOrder === 'asc' ? '↑' : '↓') : '↕';

    return (
      <Box
        style={{ cursor: 'pointer' }}
        onClick={() => handleHeaderClick(field)}
      >
        {label} {arrow}
      </Box>
    );
  };

  if (isLoading) {
    return (
      <Center mt='lg'>
        <Loader color='gray' />
      </Center>
    );
  }

  return (
    <AppShell.Main>
      <Title mb='xl' order={3}>
        Рейтинг / Отзывы{' '}
      </Title>
      <Stack>
        {!isMobile && (
          <div
            style={{
              display: 'flex',
              fontWeight: 600,
              padding: 8,
              borderBottom: '1px solid #ddd',
              gap: 12
            }}
          >
            <div style={{ flex: '0 0 50px' }}>ID</div>
            <div style={{ flex: '0 0 120px' }}>
              {renderHeader('Клубы', 'club')}
            </div>
            <div style={{ flex: '0 0 120px' }}>
              {renderHeader('Артисты', 'artist')}
            </div>
            <div style={{ flex: '0 0 200px' }}>Отзывы</div>
            <div style={{ flex: '0 0 200px' }}>Ответы Клуба</div>
            <div style={{ flex: '0 0 120px' }}>
              {renderHeader('Дата', 'date')}
            </div>
            <div style={{ flex: '0 0 150px' }}>
              {renderHeader('Оценки', 'rating')}
            </div>
          </div>
        )}

        {isMobile ? (
          <Stack gap='md'>
            {sortedRatings.map((r) => {
              const avg = (
                (r.staffAndManagement + r.dressingRoom + r.housing) /
                3
              ).toFixed(1);

              return (
                <Box
                  key={r.clubRatingId}
                  p='md'
                  style={{ border: '1px solid #eee' }}
                >
                  <Text fw={600}>{r.artistName}</Text>
                  <Text c='dimmed' fz='sm'>
                    {dayjs(r.createdAt).format('DD.MM.YYYY')}
                  </Text>

                  <Box mt='xs'>
                    <Text fw={500}>Отзыв</Text>
                    <ExpandableText
                      style={{ fontSize: '14px' }}
                      text={r.comment ?? '—'}
                    />
                  </Box>

                  <Box
                    mt='xs'
                    pl='sm'
                    style={{
                      borderLeft: '3px solid var(--mantine-primary-color-5)'
                    }}
                  >
                    <Text c='var(--mantine-primary-color-5)' fw={500}>
                      Ответ клуба <br />
                      <strong>{r.clubName}</strong>
                    </Text>
                    <ExpandableText
                      style={{ fontSize: '14px' }}
                      text={r.reply ?? '—'}
                    />
                  </Box>

                  {/* ⭐ Рейтинг */}
                  <Box mt='sm'>
                    <Text
                      c='blue'
                      fw={showRating[r.clubRatingId] ? 700 : 500}
                      size='sm'
                      style={{ cursor: 'pointer' }}
                      td='underline'
                      onClick={() =>
                        setShowRating((prev) => ({
                          ...prev,
                          [r.clubRatingId]: !prev[r.clubRatingId]
                        }))
                      }
                    >
                      Рейтинг: {avg}
                    </Text>
                    <Collapse in={showRating[r.clubRatingId]}>
                      <div style={{ marginTop: 4 }}>
                        <div>
                          Руководство и персонал:{' '}
                          {'⭐'.repeat(r.staffAndManagement)}
                        </div>
                        <div>Гримерка: {'⭐'.repeat(r.dressingRoom)}</div>
                        <div>Жильё: {'⭐'.repeat(r.housing)}</div>
                      </div>
                    </Collapse>
                  </Box>

                  {/* ✏️ Действия */}
                  <Flex gap='sm' mt='sm'>
                    <ActionIcon
                      color='blue'
                      variant='light'
                      onClick={() => handleEdit(r)}
                    >
                      <PencilIcon />
                    </ActionIcon>
                  </Flex>
                </Box>
              );
            })}
          </Stack>
        ) : (
          sortedRatings.map((r) => {
            const totalRating =
              r.staffAndManagement + r.dressingRoom + r.housing;
            const averageRating = (totalRating / 3).toFixed(1);

            return (
              <div
                key={r.clubRatingId}
                style={{
                  borderBottom: '1px solid #ddd',
                  backgroundColor: 'white',
                  padding: 8,
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 12,
                  alignItems: 'center'
                }}
              >
                <div style={{ flex: '0 0 50px' }}>{r.artistProfileId}</div>

                <div
                  style={{
                    flex: '0 0 120px',
                    fontWeight: 600,
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    lineHeight: 1.3
                  }}
                >
                  {r.clubName}
                </div>

                <div
                  style={{
                    flex: '0 0 120px',
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    lineHeight: 1.3
                  }}
                >
                  {r.artistName}
                </div>

                <div style={{ flex: '0 0 200px' }}>
                  <ExpandableText
                    style={{ fontSize: '14px' }}
                    text={r.comment ?? '—'}
                  />
                </div>
                <div style={{ flex: '0 0 200px' }}>
                  <ExpandableText
                    style={{ fontSize: '14px' }}
                    text={r.reply ?? '—'}
                  />
                </div>

                <div style={{ flex: '0 0 120px' }}>
                  {dayjs(r.createdAt).format('DD.MM.YYYY')}
                </div>

                <Box
                  style={{ flex: '0 0 120px', cursor: 'pointer' }}
                  onClick={() =>
                    setShowRating((prev) => ({
                      ...prev,
                      [r.clubRatingId]: !prev[r.clubRatingId]
                    }))
                  }
                >
                  <Flex align='center' justify='space-between'>
                    <Text
                      c='blue'
                      fw={showRating[r.clubRatingId] ? 700 : 500}
                      size='sm'
                      td='underline'
                    >
                      Рейтинг &nbsp;{averageRating}
                    </Text>
                  </Flex>
                  <Collapse in={showRating[r.clubRatingId]}>
                    <div style={{ marginTop: 4 }}>
                      <div>
                        Руководство и персонал:{' '}
                        {'⭐'.repeat(r.staffAndManagement)}
                      </div>
                      <div>Гримерка: {'⭐'.repeat(r.dressingRoom)}</div>
                      <div>Жильё: {'⭐'.repeat(r.housing)}</div>
                    </div>
                  </Collapse>
                </Box>

                <Box
                  style={{
                    flex: '0 0 70px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end'
                  }}
                >
                  <Stack align='center' gap='xs'>
                    <ActionIcon
                      color='blue'
                      variant='light'
                      onClick={() => handleEdit(r)}
                    >
                      <PencilIcon />
                    </ActionIcon>
                  </Stack>
                </Box>
              </div>
            );
          })
        )}
      </Stack>

      <Modal
        centered
        opened={!!editing}
        size='md'
        title={editing ? `Редактировать отзыв #${editing.clubRatingId}` : ''}
        onClose={() => setEditing(null)}
      >
        <Stack>
          <Textarea
            autosize
            label='Комментарий'
            minRows={5}
            value={editComment}
            onChange={(e) => setEditComment(e.currentTarget.value)}
          />
          <Textarea
            autosize
            label='Ответ клуба'
            minRows={5}
            value={editReply}
            onChange={(e) => setEditReply(e.currentTarget.value)}
          />
          <Group mt='md'>
            <Button variant='outline' onClick={() => setEditing(null)}>
              Отмена
            </Button>
            <Button
              loading={moderateClubMutation.isPending}
              onClick={handleSave}
            >
              Сохранить
            </Button>
          </Group>
        </Stack>
      </Modal>
    </AppShell.Main>
  );
};
